import {
  column,
  dateColumn,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridRowLookup,
  GridTableLayout,
  HeaderAction,
  multiFilter,
  PageHeaderLayout,
  RowStyles,
  simpleDataRows,
  SimpleHeaderAndData,
  useGridTableLayoutState,
} from "@homebound/beam";
import { MutableRefObject, useCallback, useMemo, useRef } from "react";
import { GlobalOptionsFilter, GlobalOptionStatus } from "src/generated/graphqlTypes";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { GlobalOptionsTagColumn } from "src/routes/libraries/global-options/components/GlobalOptionsTagColumn";
import { useOptionDrawer } from "src/routes/libraries/global-options/GlobalOptionDrawer";
import {
  GlobalOptionsDetailFragment,
  GlobalOptionsPageMetadataQuery,
  GlobalOptionsQuery,
  useGlobalOptionsPageMetadataQuery,
  useGlobalOptionsQuery,
} from "src/routes/libraries/global-options/GlobalOptions.generated";
import { createExportCsvUrl } from "src/RouteUrls";
import { queryResult } from "src/utils";
import { formatWithShortYear } from "src/utils/dates";
import { openNewTab } from "src/utils/window";

export function GlobalOptionsPage() {
  const query = useGlobalOptionsPageMetadataQuery();
  useDocumentTitle("Global Options");
  return queryResult(query, {
    data: ({ globalOptionTypes, locations, developments }) => (
      <GlobalOptionsView
        globalOptionTypes={globalOptionTypes}
        globalOptionsLocations={locations}
        developmentsOptions={developments}
      />
    ),
  });
}

export type GlobalOptionsViewProps = {
  globalOptionTypes: GlobalOptionsPageMetadataQuery["globalOptionTypes"];
  globalOptionsLocations: GlobalOptionsPageMetadataQuery["locations"];
  developmentsOptions: GlobalOptionsPageMetadataQuery["developments"];
};

export function GlobalOptionsView(props: GlobalOptionsViewProps) {
  const { globalOptionTypes, globalOptionsLocations, developmentsOptions } = props;
  const { openOptionDrawer } = useOptionDrawer();

  const filterDefs: FilterDefs<GlobalOptionsFilter> = useMemo(() => {
    const goStatuses = [
      { name: "Active", status: GlobalOptionStatus.Active },
      { name: "Archived", status: GlobalOptionStatus.Archived },
    ];

    const type = multiFilter({
      label: "Option type",
      // Group V1 and V2 types by name, so choosing `Spec Level` will show both V1 and V2 types
      options: globalOptionTypes
        .filter((got) => got.name !== "Plan Package")
        .groupByObject((got) => got.name)
        .map(([name, gots]) => ({ name, ids: gots.map((got) => got.id).join(",") })),
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ ids }) => ids,
    });
    const status = multiFilter({
      label: "Status",
      options: goStatuses,
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ status }) => status,
    });
    const locations = multiFilter({
      label: "Locations",
      options: globalOptionsLocations,
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    });
    const developments = multiFilter({
      label: "Developments",
      options: developmentsOptions.sortByKey("name"),
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    });

    return { type, status, locations, developments };
  }, [globalOptionTypes, globalOptionsLocations, developmentsOptions]);

  const layoutState = useGridTableLayoutState<GlobalOptionsFilter>({
    persistedFilter: { filterDefs, storageKey: "globalOptionsFilter" },
    search: "client",
  });
  const { type, ...otherFilters } = layoutState.filter;
  const query = useGlobalOptionsQuery({
    variables: {
      filter: {
        ...otherFilters,
        // If a type is multiple IDs, we need to split them and filter by each ID
        type: type?.flatMap((maybeIds) => maybeIds.split(",")),
        isPlanPackageType: false,
        isCatalogV2: false,
      },
    },
  });
  const rowLookup = useRef<GridRowLookup<Row>>();
  const rowStyles = useMemo(() => createRowStyles(rowLookup, openOptionDrawer), [openOptionDrawer]);
  const createRows = useCallback(
    (data: GlobalOptionsQuery | undefined) => simpleDataRows(data?.globalOptions ?? []),
    [],
  );
  const headerActions: HeaderAction[] = useMemo(
    () => [
      { label: "Export", variant: "secondary", onClick: () => openNewTab(`${createExportCsvUrl("globalOptions")}`) },
      {
        label: "Create",
        variant: "primary",
        onClick: () =>
          openOptionDrawer({
            refetchOnCreate: async () => {
              await query.refetch();
            },
          }),
      },
    ],
    [openOptionDrawer, query],
  );

  return (
    <PageHeaderLayout pageHeader={{ title: "Options", actions: headerActions }}>
      <GridTableLayout
        hideEditColumns
        layoutState={layoutState}
        tableProps={{
          query,
          createRows,
          columns,
          rowStyles,
          rowLookup,
          sorting: { on: "client" },
          as: "virtual",
          emptyFallback: "No global options found",
        }}
      />
    </PageHeaderLayout>
  );
}

type Row = SimpleHeaderAndData<GlobalOptionsDetailFragment>;

const columns: GridColumn<Row>[] = [
  column<Row>({ header: "Code", data: ({ code }) => code, mw: "110px" }),
  column<Row>({ header: "Internal name", data: ({ name }) => name, w: 2, mw: "240px" }),
  column<Row>({
    header: "Group",
    // For single option groups, don't needlessly repeat the name
    data: (data) => (data.group.isSingleOptionGroup ? "" : data.group.name),
    w: 1.5,
    mw: "140px",
  }),
  column<Row>({
    header: "Option type",
    data: ({ type }) =>
      // Allow version of type to be displayed
      `${type.name} (V${type.version})`,
    mw: "180px",
  }),
  column<Row>({
    header: "Location",
    data: ({ locations }) => locations.map(({ name }) => name).join(", "),
    w: 2,
    mw: "200px",
  }),
  column<Row>({
    header: "Tag",
    data: (option) => <GlobalOptionsTagColumn option={option} />,
    wrapAction: false,
    clientSideSort: false,
    w: 2,
    mw: "200px",
  }),
  column<Row>({ header: "Status", data: ({ statusDetail }) => statusDetail.name, mw: "150px" }),
  dateColumn<Row>({
    header: "Active date",
    data: ({ activeDate, archivedDate }) => ({ content: activeDateToArchivedCell(activeDate, archivedDate) }),
    mw: "180px",
    clientSideSort: false,
  }),
];

function activeDateToArchivedCell(
  activeDate: GlobalOptionsDetailFragment["activeDate"],
  archivedDate: GlobalOptionsDetailFragment["archivedDate"],
) {
  if (activeDate && archivedDate) {
    return `${formatWithShortYear(activeDate)} - ${formatWithShortYear(archivedDate)}`;
  }
  return activeDate ? `${formatWithShortYear(activeDate)} - present` : `none`;
}

function createRowStyles(
  lookup: MutableRefObject<GridRowLookup<Row> | undefined>,
  openOptionDrawer: ReturnType<typeof useOptionDrawer>["openOptionDrawer"],
): RowStyles<Row> {
  function openRow(row: GridDataRow<Row>): void {
    if (row.kind === "data") {
      const { prev, next } = lookup.current!.lookup(row)["data"];
      const globalOption = row["data"];
      const opts = { onPrevClick: prev && (() => openRow(prev)), onNextClick: next && (() => openRow(next)) };

      openOptionDrawer({ optionId: globalOption.id }, opts);
    }
  }

  return { data: { onClick: openRow } };
}
